import type { NotificationWithReadState } from './types';

/**
 * Response type for notification API calls
 */
export type NotificationsResponse = {
  notifications: NotificationWithReadState[];
  nextCursor?: string;
};

/**
 * Parameters for notification state updating
 */
export type ToggleNotificationParams = {
  notificationId: string;
  markAsRead: boolean;
};

/**
 * Parameters for batch notification state updating
 */
export type BatchToggleNotificationParams = {
  notificationIds: string[];
  markAsRead: boolean;
};

/**
 * Fetch notifications from the API
 * Supports cursor-based pagination
 *
 * @param userId User ID to fetch notifications for
 * @param cursor Optional cursor for pagination (ID of the last notification in previous page)
 * @param limit Maximum number of notifications to return per page
 * @returns Object containing notifications array and nextCursor for pagination
 */
export const fetchNotifications = async (
  userId: string | undefined,
  cursor?: string,
  limit: number = 20,
): Promise<NotificationsResponse> => {
  if (!userId) {
    return { notifications: [] };
  }

  // Build the URL with pagination parameters
  let url = `/api/notifications?userId=${encodeURIComponent(userId)}`;
  if (cursor) {
    url += `&cursor=${encodeURIComponent(cursor)}`;
  }
  if (limit !== 20) {
    // Only add if different from default
    url += `&limit=${limit}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Failed to fetch notifications');
  }

  const data = await response.json();
  return {
    notifications: data.notifications || [],
    nextCursor: data.nextCursor,
  };
};

/**
 * Update a notification's read state
 * @param notificationId The ID of the notification to update
 * @param markAsRead If true, marks as read; if false, marks as unread
 */
export const toggleNotificationReadState = async (
  notificationId: string,
  markAsRead: boolean,
): Promise<void> => {
  try {
    const response = await fetch('/api/notifications', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        notificationId,
        markAsRead,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Server returned ${response.status}`;
      throw new Error(`Failed to update notification read state: ${errorMessage}`);
    }
  } catch (error) {
    console.error(
      `Error toggling notification (ID: ${notificationId}) to ${markAsRead ? 'read' : 'unread'}:`,
      error,
    );
    throw error; // Re-throw for the caller to handle
  }
};

/**
 * Update multiple notifications' read state at once
 * @param notificationIds Array of notification IDs to update
 * @param markAsRead If true, marks as read; if false, marks as unread
 * @returns The number of notifications updated
 */
export const toggleMultipleNotificationReadStates = async (
  notificationIds: string[],
  markAsRead: boolean,
): Promise<number> => {
  try {
    const response = await fetch('/api/notifications', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        notificationIds,
        markAsRead,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Server returned ${response.status}`;
      throw new Error(`Failed to update notification read states: ${errorMessage}`);
    }

    const data = await response.json();
    return data.updatedCount || 0;
  } catch (error) {
    console.error(
      `Error batch toggling ${notificationIds.length} notifications to ${markAsRead ? 'read' : 'unread'}:`,
      error,
    );
    throw error; // Re-throw for the caller to handle
  }
};

/**
 * Mark all notifications as read for the current user
 * @returns The number of notifications marked as read
 */
export const markAllNotificationsAsRead = async (): Promise<number> => {
  try {
    const response = await fetch('/api/notifications', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        markAllAsRead: true,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Server returned ${response.status}`;
      throw new Error(`Failed to mark all notifications as read: ${errorMessage}`);
    }

    const data = await response.json();
    return data.updatedCount || 0;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error; // Re-throw for the caller to handle
  }
};
